import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Icon, Input, Link, Modal, Popover, SpaceBetween, Textarea } from '@amzn/awsui-components-react';
import { MATURITY_LABEL } from '../../../utils/maturity';
import {
  getIvtClient,
  UpdateAssetCommand,
  DeleteAssetCommand,
  AssetOrigin,
  type AssetSummary,
  type AssetDetail,
} from '../../../client/IvtHubClient';
import { isElementFilled } from '../../../utils/element';
import { denialHint } from '../../../utils/authorization';
import { setOnlyAssetFilter } from '../../MotionsPage/useMotionFilterState';
import { useChatPanel } from '../../../../context/ChatPanelContext';
import { useAssetAssessment } from '../hooks/useAssetAssessment';
import AddToStorylineModal from './AddToStorylineModal';
import AssetGrowCells from './AssetGrowCells';
import { storylineClient } from '../../../client/storylineClient';
import { kbWriteOps } from '../../../client/kbWriteOps';
import { KbDownloadDisposition } from '../../../client/IvtHubClient';

/**
 * AssetDetailHeader — title row + Details card for the Asset Detail page
 * (asset-domain-v2-design.md §3.3 / §3.4). The breadcrumb lives at the page top
 * (AssetDetailPage), spanning both columns — so this header starts at the title.
 *
 * Data source: the shared GetAsset `detail` (owned by the page's useAssetDetail),
 * NOT the list summary — GetAsset is the authoritative per-asset read (the list
 * AssetSummary can lag, e.g. sfdcTag). `asset` (summary) is only a fallback while
 * detail loads + for isNew (origin). 8-element status is computed from detail's
 * elements. SFDC tag is an editable pill (edit ALLOWED even when a tag already
 * exists — the earlier write-once lock was removed): saving issues
 * UpdateAsset({ sfdcTag }) then calls onSaved so the page reloads GetAsset and the
 * pill shows the fresh value.
 *
 * Mark/Unmark as NEW: the title row has a link-style "Mark as NEW" / "Unmark as NEW"
 * action (ticket icon) that writes assets.origin via UpdateAsset (mark = ivt_incubated,
 * unmark = ivt_legacy). Its label names the ACTION the click performs (opposite of the
 * current state). It drives the green NEW badge + Overview green bars (isNew = origin).
 *
 * Delete: the title row has a "删除 Asset" button → a confirm dialog → DeleteAsset,
 * then navigate back to /ivt/assets. It CASCADEs the asset's 8 elements / arsenal /
 * UC + Motion links (the Motions themselves are unaffected). A failure surfaces as an
 * inline error and stays on the page. DeleteAsset — like every write on this page —
 * requires the write role (DAT-8911), so a 403 is an expected outcome for a view-only
 * member, not just an unexpected 5xx.
 *
 * Motion cell: "{taggedCount} Tagged Pipelines" from AssetDetail.motionPipeline
 * (backend CR-287347358); the link opens the Motions Pipeline List tab pre-filtered
 * to this asset. (The Stalled sub-count is not shown.)
 */

// Figma stage labels append 期 except 规模化.
const STAGE_SUFFIX: Record<string, string> = { EXPLORING: '期', BUILDING: '期', PLAYBOOK: '期', SCALING: '' };

/**
 * Name-edit affordance labels. Deliberately parallel to the one-liner's
 * "…除 Asset 名称外，描述也将用于 pipeline 影响力深度评分" (see the one-liner render
 * below) so the two editable text fields explain the same "why" in the same words.
 *
 * Scope note: this names the pipeline engagement-depth score only — the chain that
 * `ScoreWorkerHandler` writes into `activity_asset_relevance` and that rolls up into
 * Influence Density, where the asset name is the scoring rubric's first criterion.
 * The four-quadrant Assessment score (`asset_assessment`.score_x/score_y) is NOT
 * affected by a rename: it is written only by the two Coral UPSERT APIs from a
 * user-triggered evaluation, its inputs are CEO-interview / company research (not the
 * name), and nothing in cachesync/cacheworker/scoring touches that table.
 */
const NAME_SCORING_HINT_EDIT = '编辑 Asset 名称，名称将用于 pipeline 影响力深度评分';
const NAME_SCORING_HINT_ADD = '添加 Asset 名称，名称将用于 pipeline 影响力深度评分';

/** Dual-axis classification from an (x, y) score pair. The threshold is 3.5 on
 *  both axes (design doc §3.9). X = 对客户的重要性; Y = 对 AWS 的重要性.
 *  Missing either → null (cell renders 尚未打分). */
type Quadrant = 'aws-driven' | 'star' | 'partner' | 'customer-driven';
const QUADRANT_LABEL: Record<Quadrant, string> = {
  'aws-driven': 'AWS 推动型',
  star: '明星 Asset',
  partner: '放弃/Partner',
  'customer-driven': '客户驱动型',
};
function classifyQuadrant(x: number | undefined, y: number | undefined): Quadrant | null {
  if (x == null || y == null) return null;
  const T = 3.5;
  if (x >= T && y >= T) return 'star';
  if (x < T && y >= T) return 'aws-driven';
  if (x >= T && y < T) return 'customer-driven';
  return 'partner';
}

export default function AssetDetailHeader({
  asset,
  detail,
  onSaved,
}: {
  asset: AssetSummary;
  /** Shared GetAsset detail (owned by the page). Authoritative for sfdcTag / name /
   *  maturity / industry / 8-element; null while loading → fall back to summary. */
  detail: AssetDetail | null;
  /** Called after a successful sfdcTag save so the parent can reload GetAsset. */
  onSaved?: () => void;
}) {
  const navigate = useNavigate();
  const { setAssetScope, prefillPrompt, chatCollapsed, toggleChat } = useChatPanel();

  const assetId = detail?.assetId ?? asset.assetId ?? null;
  // Editable display name (assets.name). State lives up here — not with the other
  // edit state further down — because the raw/display split below has to be computed
  // where the old single `name` const was: `name` is captured by goToAssetPipeline
  // and chatWithKb, which are defined before the main state block.
  const [editingName, setEditingName] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [localName, setLocalName] = useState<string | undefined>(undefined);
  // Mirrors the CURRENTLY selected assetId for in-flight writes to compare against.
  // A ref (not state) because saveName reads it after an await, where a captured
  // state value would be the stale one from the render that started the save.
  const assetIdRef = useRef(assetId);
  // TWO different values, deliberately — do not collapse them.
  //
  // `rawName` = what assets.name ACTUALLY holds ('' when unset). This is the only
  // thing the editor may seed from. Assets that predate the column have name = NULL
  // (147 rows in beta, 2 in prod) and render their useCase instead; seeding the
  // editor from the display value would put that derived useCase in the input, so a
  // user who opens the pencil and saves without typing would silently freeze the
  // derived value as a real name. That is not undoable (UpdateAsset has SET
  // semantics, there is no clear channel) and it also pulls the asset into the LLM
  // relevance-scoring candidate set, whose gate is `WHERE a.name IS NOT NULL`.
  // So: rawName empty → the UI offers "添加名称" (first-naming is a deliberate act),
  // never a pre-filled rename.
  //
  // `name` = what the header DISPLAYS, keeping the original fallback chain.
  const rawName = localName ?? (detail ? detail.name ?? '' : asset.name ?? '');
  const name =
    localName ?? detail?.name ?? detail?.useCase ?? asset.name ?? asset.useCase ?? asset.assetId ?? '—';
  const maturity = detail?.maturity ?? asset.maturity;
  const stageLabel = maturity ? `${MATURITY_LABEL[maturity]}${STAGE_SUFFIX[maturity] ?? ''}` : '—';
  // Industry vs Topic: an industry-anchored asset shows "Industry / {industry}";
  // a topic-anchored asset (no industry, has topic) shows "Topic / {topic}" — the
  // label itself flips. `topic` lives on the list summary (AssetDetail has none).
  const industry = detail?.industry ?? asset.industry;
  const isTopicAnchored = !industry && !!asset.topic;
  const anchorLabel = isTopicAnchored ? 'Topic' : 'Industry';
  const anchorValue = industry ?? asset.topic ?? '—';
  // Motion cell: taggedCount = tagged Opps across this asset's Motions (DISTINCT
  // oppty) from AssetDetail.motionPipeline (backend CR-287347358). Null while detail
  // loads. The Stalled sub-count is intentionally NOT shown (product decision).
  const taggedCount = detail?.motionPipeline?.taggedCount;

  const goToAssetPipeline = () => {
    if (!assetId) return;
    // Reset ALL Motion filters to JUST this asset, so the Pipeline List lands
    // scoped to it (not whatever the user last had selected on the Motions page,
    // which persists in sessionStorage). Then open the Pipeline List tab.
    setOnlyAssetFilter({ value: assetId, label: name });
    navigate('/ivt/motions?tab=pipeline');
  };

  // Chat with KB: bind the chat panel to THIS asset (sticky scope). Every user
  // message sent after binding carries context.asset_scope so the agent can call
  // the retrieve_asset_kb MCP tool. Also expands the panel if it's collapsed.
  // Rebinding to a different asset simply overwrites; the user clears scope from
  // the panel's toolbar chip (× button).
  const chatWithKb = () => {
    if (!assetId) return;
    setAssetScope({ assetId, assetName: name });
    if (chatCollapsed) toggleChat();
  };

  // Score Now: PRE-FILL (not send) the chat box with the scoring prompt so the
  // user can review / edit before sending. The Skill on the other side runs
  // the 打分 + 称重 flow and writes back via UpdateAssetElementScore /
  // UpdateAssetWeighing (design doc §3 交互流程). prefillPrompt also expands
  // the chat panel if it's collapsed. Disabled only while assetId is loading —
  // there'd be no asset to scope the prompt to.
  const scoreNow = () => {
    if (!assetId) return;
    prefillPrompt(`评估 IVTHub Asset ${name}(${assetId}) 对目标客户的可行性，打分并生成详细的 report。`);
  };

  // 8-element completeness: prefer detail's actual elements; else the list summary count.
  const completeness = detail?.elements
    ? detail.elements.filter((e) => isElementFilled(e.status, e.content)).length
    : asset.elementCompleteness ?? 0;

  // Assessment (八要素质量 + 称重 X/Y + 报告 URL). Independent GetAssetAssessment
  // read — absent record → assessment=null → 占位符 render. The V29 shape splits the
  // two flows (element-scored vs weighed) so a partial row is legal: either group of
  // fields may be null while the other has data. See asset-assessment-design.md §2.3.
  //   8-Element Quality           → prefer eightElementQuality (0–100 算法结果); fall
  //                                 back to 编码完整度 completeness/8 while unscored.
  //   Assessment Score (X/Y chips)→ X/Y pair (both required); missing → 占位符.
  //   评估报告 link                → weighingReportFileId (stable UUIDv3 for the
  //                                 asset's weighing report kb_files row). On click
  //                                 we mint a FRESH presigned GET via
  //                                 GetKbFileDownloadUrlGeneric (attachment) — no
  //                                 TTL to expire while the Details page sits open.
  //                                 Same download path the KB panel uses. The
  //                                 GetAssetAssessment response still carries the
  //                                 legacy weighingReportUrl (@deprecated) but the
  //                                 UI ignores it now.
  //   Dual-axis Classification    → derived from (X, Y) via classifyQuadrant; the
  //                                 label + Popover replaces the earlier Industry
  //                                 Rank cell (Figma alignment).
  const { assessment } = useAssetAssessment(assetId);
  const eightQuality = assessment?.eightElementQuality;
  const scoreX = assessment?.scoreX;
  const scoreY = assessment?.scoreY;
  const weighingReportFileId = assessment?.weighingReportFileId;

  // 评估报告 download: mint a fresh presigned GET on every click (no TTL to
  // expire while the Details page is open). Guarded by a per-page inflight
  // flag so a rapid double-click doesn't fire two sign requests + two
  // downloads. Same mechanism the KB panel uses (kbWriteOps.downloadUrl).
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [reportDownloadError, setReportDownloadError] = useState<string | null>(null);
  const downloadWeighingReport = async () => {
    if (!assetId || !weighingReportFileId || downloadingReport) return;
    setDownloadingReport(true);
    setReportDownloadError(null);
    try {
      const url = await kbWriteOps.downloadUrl(
        { ownerType: 'ASSET', ownerId: assetId },
        weighingReportFileId,
        KbDownloadDisposition.ATTACHMENT,
      );
      if (!url) throw new Error('no download url');
      // Presigned URL is a bearer token — use once via a transient <a download>;
      // never window.location / address bar / log it. The server signs
      // response-content-disposition: attachment so S3 replies attachment
      // (`<a download>` alone is only a HINT for cross-origin URLs).
      const a = document.createElement('a');
      a.href = url;
      a.download = `${assetId}-weighing-report.html`;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      setReportDownloadError('评估报告下载失败，请重试。');
    } finally {
      setDownloadingReport(false);
    }
  };

  // Editable SFDC tag. `localTag` optimistically holds the just-saved value so the
  // pill doesn't flash the stale value during the parent's reload; reset when the
  // selected asset changes.
  const [editingTag, setEditingTag] = useState(false);
  const [draftTag, setDraftTag] = useState('');
  const [savingTag, setSavingTag] = useState(false);
  const [tagError, setTagError] = useState<string | null>(null);
  const [localTag, setLocalTag] = useState<string | undefined>(undefined);
  // Editable one-line description (assets.one_liner), shown under the SFDC tag.
  // Same optimistic-local + edit-in-place pattern as the tag.
  const [editingOneLiner, setEditingOneLiner] = useState(false);
  const [draftOneLiner, setDraftOneLiner] = useState('');
  const [savingOneLiner, setSavingOneLiner] = useState(false);
  const [oneLinerError, setOneLinerError] = useState<string | null>(null);
  const [localOneLiner, setLocalOneLiner] = useState<string | undefined>(undefined);
  // Delete-asset confirmation dialog + in-flight / error state.
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Add-to-Storyline modal. The asset's industry (detail authoritative, summary
  // fallback); a Topic-anchored / null-industry asset has none → the button is
  // disabled (a storyline is a per-industry concept, so there's nothing to link to).
  const [addingToStoryline, setAddingToStoryline] = useState(false);
  const assetIndustry = detail?.industry ?? asset.industry ?? '';
  const isIndustryAnchored = assetIndustry.length > 0;
  // The storyline badges under the SFDC tag. storylineLinks is a GetAsset (detail)
  // field; each × unlinks that one storyline. `unlinkingId` = the badge whose unlink
  // is in flight (its × shows a spinner + disables).
  const storylineLinks = detail?.storylineLinks ?? [];
  const [unlinkingId, setUnlinkingId] = useState<string | null>(null);
  async function unlinkStoryline(storylineId: string) {
    if (!assetId) return;
    // Which asset this unlink belongs to — the header is NOT remounted when the user
    // picks another asset in the tree, so without this the error would land on whichever
    // asset is selected when the promise settles (same hazard saveName documents).
    const forAssetId = assetId;
    setUnlinkingId(storylineId);
    setActionError(null);
    try {
      await storylineClient.unlinkAsset(forAssetId, storylineId);
      onSaved?.(); // reload GetAsset → the badge row + tree reflect the removed link
    } catch (e) {
      // Was try/finally with no catch: the caller does `void unlinkStoryline(...)`, so a
      // rejection became an unhandled promise rejection — the spinner cleared, the badge
      // stayed, and the UI looked like a no-op. UnlinkStorylineAsset is write-gated now,
      // which makes that silent path reachable for any view-only member.
      if (assetIdRef.current !== forAssetId) return;
      setActionError(denialHint(e) ?? ((e as Error).message || '移除 Storyline 关联失败，请重试。'));
    } finally {
      setUnlinkingId(null);
    }
  }
  // New-Asset toggle (origin): optimistic just-set value + in-flight flag.
  const [localIsNew, setLocalIsNew] = useState<boolean | undefined>(undefined);
  const [savingOrigin, setSavingOrigin] = useState(false);
  // Shared error slot for the two header actions that have no field of their own to
  // hang a message on (the NEW toggle and the storyline-badge ×). Rendered once at the
  // top of the header rather than duplicating an inline error next to each icon button.
  const [actionError, setActionError] = useState<string | null>(null);
  useEffect(() => {
    setLocalTag(undefined);
    setEditingTag(false);
    setTagError(null);
    // Reset one-liner edit state when the selected asset changes.
    setLocalOneLiner(undefined);
    setEditingOneLiner(false);
    setOneLinerError(null);
    // Reset delete UI when the selected asset changes.
    setConfirmingDelete(false);
    setDeleting(false);
    setDeleteError(null);
    // Reset the optimistic New value when the selected asset changes.
    setLocalIsNew(undefined);
    // Same reason as the per-field errors: a message about asset A must not linger
    // over asset B (the header is not remounted on an assetId change).
    setActionError(null);
    // Likewise the unlink spinner: the storyline badges are `disabled={unlinkingId != null}`,
    // so an in-flight unlink on asset A would otherwise disable ALL of asset B's × buttons
    // until A's request settles.
    setUnlinkingId(null);
    // Reset name edit state when the selected asset changes.
    setLocalName(undefined);
    setEditingName(false);
    setNameError(null);
    setSavingName(false);
    // Lets an in-flight rename detect that it no longer owns the header (see saveName).
    assetIdRef.current = assetId;
  }, [assetId]);
  // Tag value: an optimistic just-saved value wins; otherwise the AUTHORITATIVE
  // GetAsset detail once loaded (its sfdcTag, possibly empty = truly no tag). Only
  // fall back to the list summary WHILE detail is still loading — after it lands,
  // the summary's (possibly stale) value must not resurrect a "tag" that detail says
  // is empty (that would wrongly flip an add-able asset to read-only).
  const tag = localTag ?? (detail ? detail.sfdcTag ?? '' : asset.sfdcTag ?? '');

  const startTagEdit = () => {
    setDraftTag(tag);
    setTagError(null);
    setEditingTag(true);
  };
  const cancelTagEdit = () => {
    setEditingTag(false);
    setTagError(null);
  };
  const saveTag = async () => {
    if (!assetId) return;
    const next = draftTag.trim();
    setSavingTag(true);
    setTagError(null);
    try {
      await getIvtClient().send(new UpdateAssetCommand({ assetId, sfdcTag: next }));
      setLocalTag(next);
      setEditingTag(false);
      onSaved?.();
    } catch (e) {
      setTagError(denialHint(e) ?? ((e as Error).message || '保存失败'));
    } finally {
      setSavingTag(false);
    }
  };

  // One-line description: optimistic local wins; else the AUTHORITATIVE GetAsset
  // detail (oneLiner is detail-only — not on the list summary). Same add/edit-in-
  // place UX as the tag; UpdateAsset({ oneLiner }) with omit-to-leave-unchanged.
  const oneLiner = localOneLiner ?? detail?.oneLiner ?? '';
  const startOneLinerEdit = () => {
    setDraftOneLiner(oneLiner);
    setOneLinerError(null);
    setEditingOneLiner(true);
  };
  const cancelOneLinerEdit = () => {
    setEditingOneLiner(false);
    setOneLinerError(null);
  };
  const saveOneLiner = async () => {
    if (!assetId) return;
    const next = draftOneLiner.trim();
    setSavingOneLiner(true);
    setOneLinerError(null);
    try {
      await getIvtClient().send(new UpdateAssetCommand({ assetId, oneLiner: next }));
      setLocalOneLiner(next);
      setEditingOneLiner(false);
      onSaved?.();
    } catch (e) {
      setOneLinerError(denialHint(e) ?? ((e as Error).message || '保存失败'));
    } finally {
      setSavingOneLiner(false);
    }
  };

  // Display name: same optimistic-local + edit-in-place pattern as the tag, seeded
  // from rawName (see the rawName/name comment above — never from the display value).
  const NAME_MAX = 512; // matches UpdateAsset.name @length(max: 512)
  const draftNameTrimmed = draftName.trim();
  // Save is blocked on exactly what the server rejects, so the user never sees a raw
  // 400: blank (Smithy @length(min:1) + the activity-layer strip guard) and over-long
  // (@length(max:512)). Cloudscape Input has no maxLength prop, so length is checked
  // here rather than capped while typing — silently truncating a pasted name would
  // hide the problem.
  const nameTooLong = draftNameTrimmed.length > NAME_MAX;
  const nameSaveDisabled = savingName || draftNameTrimmed.length === 0 || nameTooLong;
  const startNameEdit = () => {
    setDraftName(rawName);
    setNameError(null);
    setEditingName(true);
  };
  const cancelNameEdit = () => {
    setEditingName(false);
    setNameError(null);
  };
  /**
   * Maps a failed rename to a message the user can act on. Authorization denials are
   * delegated to the shared `denialHint` (see utils/authorization.ts); the branches
   * below are rename-specific.
   *
   * Both 400 shapes must be handled: Smithy input validation rejects an empty or
   * over-512 name as `ValidationException`, while the activity-layer guard rejects a
   * name with no printable character (spaces, NBSP, zero-width) as
   * `InvalidInputException` — verified against beta. Matching only one leaks a raw
   * error message. 409 is detected by the error's `name` string, same as
   * StorylineModal / useSolutionMapMutations, so no regenerated client is needed.
   */
  const nameErrorHint = (e: unknown, attempted: string): string => {
    const kind = (e as { name?: string }).name ?? '';
    if (kind === 'ConflictException') {
      // Uniqueness is case- and whitespace-insensitive (uq_assets_name_ci on
      // lower(btrim(name))), so the colliding asset's name may only differ in case —
      // hence "已存在" rather than "名称重复", and the hint says to search case-
      // insensitively since the taken name may not appear verbatim in the list.
      return `已存在名为「${attempted}」的 Asset（不区分大小写与首尾空格），请换一个名字。`;
    }
    if (kind === 'ValidationException' || kind === 'InvalidInputException') {
      return `名称不合法：不能为空白，且长度不超过 ${NAME_MAX} 个字符。`;
    }
    // NOT retryable, so "请重试" would be actively wrong advice.
    if (kind === 'ResourceNotFoundException') {
      return '该 Asset 已不存在（可能已被删除），请返回列表刷新。';
    }
    if (kind === 'ThrottlingException') {
      return '请求过于频繁，请稍后重试。';
    }
    // 403 is likewise not retryable, and there are four distinct causes with different
    // remedies (missing write role vs industry scope vs not-a-member vs a service
    // config gap) — hence the shared classifier rather than one string. It also picks
    // up the membership denial, whose error name is 'Unknown', not 'ForbiddenException'.
    const denied = denialHint(e);
    if (denied) return denied;
    // Keep the server's message instead of swallowing it — an unmodelled failure is
    // exactly the case where the raw text is the only diagnostic. Matches saveTag /
    // saveOneLiner in this file. `||` not `??`: a Smithy exception always defines
    // `.message`, so `??` would let an empty message render a blank error.
    return (e as Error).message || '保存失败，请重试。';
  };
  const saveName = async () => {
    if (!assetId || nameSaveDisabled) return;
    const next = draftNameTrimmed;
    // No-op guard: submitting the unchanged name would still bump updated_by /
    // updated_at and append a history row for nothing.
    if (next === rawName) {
      setEditingName(false);
      return;
    }
    // Which asset this save belongs to. The header is NOT remounted when the user
    // picks another asset in the tree (same route, only the :assetId param changes,
    // and AssetDetailPage renders it without a key), so a slow rename would otherwise
    // land its setState on whatever asset is selected when the promise settles:
    // setLocalName would put A's name on B's title — persistently, since localName is
    // only cleared on the NEXT assetId change — and a failure would set nameError on B
    // where the editor is already closed, swallowing A's error entirely.
    const forAssetId = assetId;
    setSavingName(true);
    setNameError(null);
    try {
      await getIvtClient().send(new UpdateAssetCommand({ assetId: forAssetId, name: next }));
      if (assetIdRef.current !== forAssetId) return;
      setLocalName(next);
      setEditingName(false);
      // Reloads BOTH GetAsset and ListAssets (page's handleSaved) and bumps the UC
      // arsenal's refresh key — the arsenal renders this asset's name too.
      onSaved?.();
    } catch (e) {
      if (assetIdRef.current !== forAssetId) return;
      setNameError(nameErrorHint(e, next));
    } finally {
      // Gated on ownership like the branches above. savingName is ONE shared flag, so
      // clearing it unconditionally would release a DIFFERENT asset's in-flight lock:
      // rename A, switch to B, start renaming B, then A settles → B's ✓ re-enables and
      // its spinner stops while B's request is still pending (double-submit). Not
      // clearing it here is safe because the assetId effect already resets the flag on
      // every switch, so no stuck spinner survives.
      if (assetIdRef.current === forAssetId) setSavingName(false);
    }
  };

  // New-Asset flag from the AUTHORITATIVE origin (detail once loaded, else summary),
  // with an optimistic override so the toggle doesn't flip back during the reload.
  // origin values are lower-case wire strings (ivt_incubated = New).
  const originValue = (detail?.origin ?? asset.origin ?? '').toLowerCase();
  const assetIsNew = localIsNew ?? (originValue === AssetOrigin.IVT_INCUBATED);

  // Toggle New: set origin to ivt_incubated (on) or ivt_legacy (off) via UpdateAsset.
  // Off always lands on ivt_legacy (product decision) regardless of the prior
  // non-New origin (partner/global) — the New toggle is a two-state control.
  const toggleNew = async (next: boolean) => {
    if (!assetId || savingOrigin) return;
    const origin = next ? AssetOrigin.IVT_INCUBATED : AssetOrigin.IVT_LEGACY;
    const forAssetId = assetId;
    setSavingOrigin(true);
    setActionError(null);
    setLocalIsNew(next); // optimistic
    try {
      await getIvtClient().send(new UpdateAssetCommand({ assetId: forAssetId, origin }));
      onSaved?.();
    } catch (e) {
      // Ownership check FIRST, like saveName: when the header no longer owns this write,
      // neither the revert nor the message may be applied. Reverting below the guard
      // would write asset A's rollback onto asset B's optimistic value (the [assetId]
      // effect has already cleared it).
      if (assetIdRef.current !== forAssetId) return;
      setLocalIsNew(!next); // revert on failure
      // Reverting alone is indistinguishable from "the click didn't register" — with
      // UpdateAsset now write-gated, a view-only member would see the toggle bounce
      // back forever with no explanation. Say why.
      setActionError(denialHint(e) ?? ((e as Error).message || '切换 NEW 标记失败，请重试。'));
    } finally {
      setSavingOrigin(false);
    }
  };

  const confirmDelete = async () => {
    if (!assetId) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await getIvtClient().send(new DeleteAssetCommand({ assetId }));
      // Deleted (CASCADEs 8-element / arsenal / UC + Motion links server-side; the
      // Motions themselves are unaffected). The detail page for this id no longer
      // exists → go back to the Overview.
      navigate('/ivt/assets');
    } catch (e) {
      // DeleteAsset now requires the write role, so a failure here is no longer only
      // "network / 5xx": a 403 is expected for a view-only member and must NOT be
      // reported as retryable. Anything else still shows the server's own message.
      setDeleteError(denialHint(e) ?? ((e as Error).message || '删除失败，请重试。'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="ivt-assetd-header">
      {actionError && (
        <Alert type="error" dismissible onDismiss={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}
      <div className="ivt-assetd-title">
        {assetIsNew && <span className="ivt-assetd-badge-new">NEW</span>}
        {/* Display name — editable in place. Three states, mirroring the SFDC tag:
            editing / has a name (rename) / no name yet (first-naming). The
            first-naming branch is visually distinct on purpose: these assets show
            their useCase today, and the editor must not pre-fill that derived value
            (see the rawName comment). */}
        {editingName ? (
          <div className="ivt-assetd-name-edit">
            <div className="ivt-assetd-name-edit__row">
              <div className="ivt-assetd-name__input">
                <Input
                  value={draftName}
                  onChange={({ detail }) => {
                    setDraftName(detail.value);
                    // Drop a stale server error as soon as the value changes: the 409
                    // text names the PREVIOUSLY submitted value, so keeping it would
                    // contradict what is now in the box.
                    if (nameError) setNameError(null);
                  }}
                  placeholder="输入 Asset 名称…"
                  disabled={savingName}
                  ariaLabel="Asset 名称"
                  autoFocus
                  onKeyDown={({ detail }) => {
                    if (detail.key === 'Enter' && !detail.isComposing) saveName();
                    if (detail.key === 'Escape') cancelNameEdit();
                  }}
                />
              </div>
              <Button
                variant="inline-icon"
                iconName="check"
                ariaLabel="保存 Asset 名称"
                loading={savingName}
                disabled={nameSaveDisabled}
                onClick={saveName}
              />
              <Button
                variant="inline-icon"
                iconName="close"
                ariaLabel="取消编辑 Asset 名称"
                disabled={savingName}
                onClick={cancelNameEdit}
              />
            </div>
            {/* Local validation hints take precedence over a server error — they say
                why ✓ is greyed out RIGHT NOW. Both disabling conditions need a hint:
                with only the over-length one, clearing the field greyed ✓ and made
                Enter a no-op with nothing on screen explaining either.
                role="alert" because Cloudscape's Input exposes no ariaDescribedby /
                invalid prop, so this is the only way a screen-reader user hears the
                409 / 400 reason. */}
            {nameTooLong ? (
              <div className="ivt-assetd-name__error" role="alert">
                <Icon name="status-warning" size="small" variant="error" /> 名称不能超过 {NAME_MAX} 个字符（当前{' '}
                {draftNameTrimmed.length}）
              </div>
            ) : draftNameTrimmed.length === 0 ? (
              <div className="ivt-assetd-name__error" role="alert">
                <Icon name="status-warning" size="small" variant="error" /> 名称不能为空白
              </div>
            ) : (
              nameError && (
                <div className="ivt-assetd-name__error" role="alert">
                  <Icon name="status-warning" size="small" variant="error" /> {nameError}
                </div>
              )
            )}
          </div>
        ) : rawName ? (
          // hover tooltip (native title + the button's ariaLabel) carries the "why",
          // mirroring the one-liner affordance below: the name is an input to the
          // pipeline engagement-depth scoring. Phrased without the one-liner's
          // "除…也" because the name is the primary signal, not the secondary one.
          <>
            {/* The name is ellipsis-capped, so the tooltip must lead with the FULL
                name — otherwise a truncated long name becomes unreadable anywhere in
                the UI. The scoring hint follows it. */}
            <span className="ivt-assetd-name" title={`${name} — ${NAME_SCORING_HINT_EDIT}`}>
              {name}
            </span>
            <Button variant="inline-icon" iconName="edit" ariaLabel={NAME_SCORING_HINT_EDIT} onClick={startNameEdit} />
          </>
        ) : (
          // assets.name is unset → the header is showing the derived useCase. Offer
          // first-naming, and do NOT pre-fill the derived value. Unlike the one-liner's
          // empty state, the full sentence is NOT the visible label — the title row
          // already carries the derived name plus four action buttons, so the hint
          // lives only in title/ariaLabel and the visible CTA stays short.
          <>
            <span className="ivt-assetd-name" title={NAME_SCORING_HINT_ADD}>
              {name}
            </span>
            <Button
              variant="inline-link"
              iconName="edit"
              ariaLabel={NAME_SCORING_HINT_ADD}
              disabled={!assetId}
              onClick={startNameEdit}
            >
              {/* Visible label must be a prefix of the ariaLabel (WCAG 2.5.3 Label in
                  Name) — an ariaLabel that drops the visible words would leave a
                  speech-input user unable to address this control. Hence "添加 Asset
                  名称", not "添加名称". */}
              添加 Asset 名称
            </Button>
          </>
        )}
        {/* Title-row actions live at the far right (spacer pushes them over).
            alignItems:center keeps the link-style action vertically centred with
            the bordered Buttons (different intrinsic heights). */}
        <span className="ivt-assetd-title__spacer" />
        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
          {/* Mark / Unmark as NEW — writes origin via UpdateAsset (mark =
              ivt_incubated, unmark = ivt_legacy). The label states the ACTION the
              click performs (opposite of the current state): NEW → "Unmark as NEW",
              not-NEW → "Mark as NEW". Drives the green NEW badge (isNew = origin).
              Disabled while assetId loads or a write is in flight. */}
          <Button
            variant="inline-link"
            iconName="ticket"
            loading={savingOrigin}
            disabled={!assetId}
            onClick={() => void toggleNew(!assetIsNew)}
          >
            {assetIsNew ? 'Unmark as NEW' : 'Mark as NEW'}
          </Button>
          {/* Add to Storyline: link this asset to storylines in its OWN industry.
              Disabled while assetId loads OR when the asset is not industry-anchored
              (Topic-anchored / null industry) — a storyline is a per-industry concept,
              so there's nothing valid to link to. */}
          <Button
            iconName="add-plus"
            disabled={!assetId || !isIndustryAnchored}
            // Hover/focus tooltip explaining WHY it's greyed out (Cloudscape renders
            // disabledReason only while disabled). Topic-anchored assets have no
            // industry → no storyline to link to.
            disabledReason={
              assetId && !isIndustryAnchored
                ? '该 Asset 未归属行业（Topic 锚定），无法关联 Storyline'
                : undefined
            }
            onClick={() => setAddingToStoryline(true)}
          >
            Add to Storyline
          </Button>
          {/* Chat with KB: binds the panel to this asset (sticky scope). Every
              subsequent user message carries the asset info in context.items
              (AG-UI convention) so the agent can call retrieve_asset_kb. Also
              expands the panel if it's collapsed. gen-ai icon signals the AI
              action. Disabled only while assetId is still loading. */}
          <Button iconName="gen-ai" disabled={!assetId} onClick={chatWithKb}>
            Chat with KB
          </Button>
          {/* Delete — destructive → opens a confirm dialog, never deletes on first click. */}
          <Button
            iconName="remove"
            disabled={!assetId}
            onClick={() => {
              setDeleteError(null);
              setConfirmingDelete(true);
            }}
          >
            删除 Asset
          </Button>
        </SpaceBetween>
      </div>

      {/* SFDC tag + storyline badges on ONE wrapping row (Figma): the tag pill first,
          then the yellow storyline badges. Both are ellipsis-capped (tag max-width
          100%, badge name 200px), and the row flex-wraps, so a long tag / many badges
          degrade gracefully instead of overflowing. */}
      <div className="ivt-assetd-tagrow">
      {/* SFDC tag. Editable whether or not a tag already exists — an existing tag shows
          the value + a pencil to change it; an empty tag shows an "add" affordance. */}
      {editingTag ? (
        <div className="ivt-assetd-tag-edit">
          <div className="ivt-assetd-tag-edit__row">
            <div className="ivt-assetd-tag__input">
              <Input
                value={draftTag}
                onChange={({ detail }) => setDraftTag(detail.value)}
                placeholder="输入 SFDC tag…"
                disabled={savingTag}
                ariaLabel="SFDC tag"
                autoFocus
                onKeyDown={({ detail }) => {
                  if (detail.key === 'Enter' && !detail.isComposing) saveTag();
                  if (detail.key === 'Escape') cancelTagEdit();
                }}
              />
            </div>
            <Button
              variant="inline-icon"
              iconName="check"
              ariaLabel="保存 SFDC tag"
              loading={savingTag}
              onClick={saveTag}
            />
            <Button
              variant="inline-icon"
              iconName="close"
              ariaLabel="取消编辑 SFDC tag"
              disabled={savingTag}
              onClick={cancelTagEdit}
            />
          </div>
          {tagError && (
            <div className="ivt-assetd-tag__error">
              <Icon name="status-warning" size="small" variant="error" /> {tagError}
            </div>
          )}
        </div>
      ) : tag ? (
        // Has a tag → show it + a pencil to CHANGE it (edit is allowed now).
        <div className="ivt-assetd-tag" onClick={startTagEdit} title="编辑 SFDC tag">
          <span className="ivt-assetd-tag__text">{tag}</span>
          <Button
            variant="inline-icon"
            iconName="edit"
            ariaLabel="编辑 SFDC tag"
            onClick={(e) => {
              e.stopPropagation();
              startTagEdit();
            }}
          />
        </div>
      ) : (
        // No tag → the "add" affordance.
        <div className="ivt-assetd-tag" onClick={startTagEdit} title="添加 SFDC tag">
          <span className="ivt-assetd-tag__text ivt-assetd-tag__text--empty">添加 SFDC tag</span>
          <Button
            variant="inline-icon"
            iconName="edit"
            ariaLabel="添加 SFDC tag"
            onClick={(e) => {
              e.stopPropagation();
              startTagEdit();
            }}
          />
        </div>
      )}

      {/* Storyline badges — the storylines this asset belongs to. Same pill component
          as the SFDC tag (Figma), tinted yellow (#F2CD54); each × unlinks that storyline
          (UnlinkStorylineAsset). Inline after the tag on the shared row above; add links
          via "Add to Storyline" (title-row button). */}
      {storylineLinks.map((l) => (
        <span
          key={l.storylineId}
          className="ivt-assetd-tag ivt-assetd-tag--storyline"
          title={l.storylineName ?? l.storylineId}
        >
          <span className="ivt-assetd-tag__text">{l.storylineName ?? l.storylineId}</span>
          <Button
            variant="inline-icon"
            iconName="close"
            ariaLabel={`移除 Storyline 关联：${l.storylineName ?? l.storylineId}`}
            loading={unlinkingId === l.storylineId}
            disabled={unlinkingId != null}
            onClick={() => l.storylineId && void unlinkStoryline(l.storylineId)}
          />
        </span>
      ))}
      </div>

      {/* One-line description under the SFDC tag. Same add / edit-in-place pattern
          as the tag; a Textarea (a sentence, not a token) with Enter=save,
          Escape=cancel. Only shown once GetAsset detail has loaded (oneLiner is
          detail-only). */}
      {detail && (editingOneLiner ? (
        <div className="ivt-assetd-oneliner-edit">
          <div className="ivt-assetd-oneliner-edit__row">
            <div className="ivt-assetd-oneliner__input">
              <Textarea
                value={draftOneLiner}
                onChange={({ detail: d }) => setDraftOneLiner(d.value)}
                placeholder="用一句话描述这个 Asset…"
                disabled={savingOneLiner}
                ariaLabel="一句话描述"
                rows={2}
                autoFocus
                onKeyDown={({ detail: d }) => {
                  // Enter saves (plain, like the tag); Shift+Enter still inserts a
                  // newline (Cloudscape default — we don't intercept it).
                  if (d.key === 'Enter' && !d.shiftKey) saveOneLiner();
                  if (d.key === 'Escape') cancelOneLinerEdit();
                }}
              />
            </div>
            <Button
              variant="inline-icon"
              iconName="check"
              ariaLabel="保存一句话描述"
              loading={savingOneLiner}
              onClick={saveOneLiner}
            />
            <Button
              variant="inline-icon"
              iconName="close"
              ariaLabel="取消编辑一句话描述"
              disabled={savingOneLiner}
              onClick={cancelOneLinerEdit}
            />
          </div>
          {oneLinerError && (
            <div className="ivt-assetd-tag__error">
              <Icon name="status-warning" size="small" variant="error" /> {oneLinerError}
            </div>
          )}
        </div>
      ) : oneLiner ? (
        // hover tooltip (native title, from the div and the button's ariaLabel) also
        // carries the "why" — the one-liner feeds the pipeline engagement-depth score.
        <div
          className="ivt-assetd-oneliner"
          onClick={startOneLinerEdit}
          title="编辑一句话描述，除 Asset 名称外，描述也将用于 pipeline 影响力深度评分"
        >
          <span className="ivt-assetd-oneliner__text">{oneLiner}</span>
          <Button
            variant="inline-icon"
            iconName="edit"
            ariaLabel="编辑一句话描述，除 Asset 名称外，描述也将用于 pipeline 影响力深度评分"
            onClick={(e) => {
              e.stopPropagation();
              startOneLinerEdit();
            }}
          />
        </div>
      ) : (
        // Empty-state prompt: CTA + why it matters. The "why" (used by the pipeline
        // engagement-depth scoring signal) is inlined here — a subtle nudge, no
        // separate hint row / help icon. Kept as a single clickable chip.
        <div
          className="ivt-assetd-oneliner"
          onClick={startOneLinerEdit}
          title="添加一句话描述，除 Asset 名称外，描述也将用于 pipeline 影响力深度评分"
        >
          <span className="ivt-assetd-oneliner__text ivt-assetd-oneliner__text--empty">
            添加一句话描述，除 Asset 名称外，描述也将用于 pipeline 影响力深度评分
          </span>
          <Button
            variant="inline-icon"
            iconName="edit"
            ariaLabel="添加一句话描述，除 Asset 名称外，描述也将用于 pipeline 影响力深度评分"
            onClick={(e) => {
              e.stopPropagation();
              startOneLinerEdit();
            }}
          />
        </div>
      ))}

      {/* "Details" title sits OUTSIDE the card (h2, same as Information / Related).
          The card holds two rows of 4 cells; columns are divided by a vertical rule.
          Row 2 follows Figma 4259:48345: Asset Last Edited and Assessment Time gave way to
          the two GROW cells (user, 2026-10-12).
          (Score Now moved next to the Assessment Score metric it triggers — below.) */}
      <div className="ivt-assetd-details-bar">
        <h2 className="ivt-assetd-details-title">Details</h2>
      </div>
      <div className="ivt-assetd-details-card">
        <div className="ivt-assetd-details">
          {/* Row 1 */}
          <div className="ivt-assetd-details__cell">
            <span className="ivt-assetd-details__label">Stage</span>
            <span className="ivt-assetd-details__value">{stageLabel}</span>
          </div>
          <div className="ivt-assetd-details__cell">
            {/* Industry-anchored → "Industry"; Topic-anchored → "Topic" (label flips). */}
            <span className="ivt-assetd-details__label">{anchorLabel}</span>
            <span className="ivt-assetd-details__value">{anchorValue}</span>
          </div>
          <div className="ivt-assetd-details__cell">
            <span className="ivt-assetd-details__label">Pipeline</span>
            {/* taggedCount from AssetDetail.motionPipeline; the link opens the
                Motions Pipeline List tab pre-filtered to this asset. (Stalled
                sub-count intentionally not shown — product decision.) */}
            <span className="ivt-assetd-details__value">
              <Link variant="primary" onFollow={goToAssetPipeline}>
                {taggedCount ?? 0} Pipelines
              </Link>
            </span>
          </div>
          <div className="ivt-assetd-details__cell">
            <span className="ivt-assetd-details__label">8-Element Quality</span>
            {/* Prefer the assessment's algorithmic quality (0–100 from the Skill's
                打分); fall back to编码完整度 completeness/8 while unscored so the
                cell stays informative before the first scoring. */}
            <span className="ivt-assetd-details__value">
              {eightQuality != null
                ? `${Math.round(eightQuality)}%`
                : `${Math.round((completeness / 8) * 100)}%`}
            </span>
          </div>

          {/* Row 2 */}
          <div className="ivt-assetd-details__cell">
            <span className="ivt-assetd-details__label ivt-assetd-details__label-row ivt-assetd-details__label-row--score">
              Assessment Score
              {/* Score Now: pre-fills the chat box with the scoring prompt for this
                  asset (agent then runs 打分 + 称重 flow via MCP tools). A small,
                  secondary inline action next to the metric it (re)computes — a
                  native button (not the Cloudscape Button, whose 14px/700 text +
                  fixed 16px icon read heavier than the label) so the 12px text +
                  11px refresh icon are sized in CSS. Disabled while assetId is
                  loading — no asset to reference. */}
              <button
                type="button"
                className="ivt-assetd-scorenow"
                disabled={!assetId}
                onClick={scoreNow}
              >
                Score Now
                <Icon name="refresh" size="small" />
              </button>
            </span>
            {/* Figma: two chips (blue X=, yellow Y=) + a "评估报告" link that
                DOWNLOADS the weighing HTML report (product decision — no in-tab
                preview, always download to disk). X = 对客户的重要性; Y = 对 AWS
                的重要性 (design doc §0.3 D9). Both scores required for the chip
                pair; missing either → 尚未打分 占位符. The report link is
                independent: shown whenever weighingReportFileId is present. */}
            {scoreX != null && scoreY != null ? (
              <span className="ivt-assetd-details__value ivt-assetd-details__score-row">
                <span className="ivt-assetd-chip ivt-assetd-chip--x">X={scoreX}分</span>
                <span className="ivt-assetd-chip ivt-assetd-chip--y">Y={scoreY}分</span>
                {weighingReportFileId && assetId && (
                  <Link
                    // href="#": Link needs an href for keyboard focus + semantics,
                    // but the real navigation is the transient <a download> the
                    // click handler synthesizes. preventDefault stops the hash
                    // jump so the URL bar doesn't change.
                    href="#"
                    variant="primary"
                    onFollow={(e) => {
                      e.preventDefault();
                      void downloadWeighingReport();
                    }}
                  >
                    {downloadingReport ? '评估报告 …' : '评估报告'}
                  </Link>
                )}
                {reportDownloadError && (
                  <span className="ivt-assetd-details__value ivt-assetd-details__value--muted">
                    {reportDownloadError}
                  </span>
                )}
              </span>
            ) : (
              <span className="ivt-assetd-details__value ivt-assetd-details__value--muted">
                尚未打分
              </span>
            )}
          </div>
          <div className="ivt-assetd-details__cell">
            <span className="ivt-assetd-details__label ivt-assetd-details__label-row">
              Dual-axis Classification
              <Popover
                dismissButton={false}
                position="top"
                size="medium"
                triggerType="custom"
                content={
                  <div className="ivt-assetd-quadrant-help">
                    <p className="ivt-assetd-quadrant-help__intro">
                      按 X (对客户的重要性) 与 Y (对 AWS 的重要性) 的 3.5 阈值划分四象限：
                    </p>
                    <ul>
                      <li><strong>明星 Asset</strong>：X ≥ 3.5 且 Y ≥ 3.5</li>
                      <li><strong>AWS 推动型</strong>：X &lt; 3.5 且 Y ≥ 3.5</li>
                      <li><strong>客户驱动型</strong>：X ≥ 3.5 且 Y &lt; 3.5</li>
                      <li><strong>放弃/Partner</strong>：X &lt; 3.5 且 Y &lt; 3.5</li>
                    </ul>
                  </div>
                }
              >
                <button type="button" className="ivt-assetd-info-btn" aria-label="四象限分类说明">
                  <Icon name="status-info" size="small" variant="link" />
                </button>
              </Popover>
            </span>
            {(() => {
              const q = classifyQuadrant(scoreX, scoreY);
              return q ? (
                <span className="ivt-assetd-details__value">{QUADRANT_LABEL[q]}</span>
              ) : (
                <span className="ivt-assetd-details__value ivt-assetd-details__value--muted">
                  尚未打分
                </span>
              );
            })()}
          </div>
          {/* GROW Type + 衡量指标 (DAT-9116 / DAT-9158). Detail is authoritative once loaded;
              the list summary carries the same three members until then. */}
          <AssetGrowCells
            assetId={assetId}
            growTypes={detail ? detail.growTypes : asset.growTypes}
            retainReview={detail ? detail.retainReview : asset.retainReview}
            growMetrics={detail ? detail.growMetrics : asset.growMetrics}
            onSaved={onSaved}
          />
        </div>
      </div>

      {/* Delete confirmation — destructive, so require an explicit confirm. The list
          spells out what goes with the asset: its UC / Topic / Motion links (the
          Motions themselves survive), the 8 elements and KB files. (Arsenal is left
          off the copy — "弹药" reads as an asset itself and is covered by "UC 关联".)
          Any failure shows here as an inline error (deleteError) instead of navigating. */}
      <Modal
        visible={confirmingDelete}
        onDismiss={() => setConfirmingDelete(false)}
        header="删除 Asset"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" disabled={deleting} onClick={() => setConfirmingDelete(false)}>
                取消
              </Button>
              <Button variant="primary" loading={deleting} onClick={() => void confirmDelete()}>
                删除
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="s">
          {deleteError && <Alert type="error">{deleteError}</Alert>}
          <span>
            确定删除「{name}」吗？删除后，该 Asset 与以下内容的关联会一并解除、相关数据将被清除：
          </span>
          <ul className="ivt-assetd-delete-list">
            <li>与 Use Case、Topic 的关联</li>
            <li>与 Motion 的关联（Motion 本身不受影响）</li>
            <li>八要素内容与 Knowledge Base 文件</li>
          </ul>
          <span>此操作不可恢复。</span>
        </SpaceBetween>
      </Modal>

      {/* Add to Storyline — link this asset to storylines in its industry. Only
          mounted for industry-anchored assets (button disabled otherwise). On save,
          reload GetAsset so the header + tree reflect the new links. */}
      {addingToStoryline && assetId && isIndustryAnchored && (
        <AddToStorylineModal
          assetId={assetId}
          industry={assetIndustry}
          currentStorylineIds={(detail?.storylineLinks ?? [])
            .map((l) => l.storylineId)
            .filter((id): id is string => !!id)}
          onClose={() => setAddingToStoryline(false)}
          onSaved={() => onSaved?.()}
        />
      )}
    </div>
  );
}
